function isRecord(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function requiredIdentifier(value, name) {
  if (typeof value !== "string" || value.length === 0 || value.trim() !== value) {
    throw new TypeError(`${name} must be a non-empty identifier without surrounding whitespace`);
  }
  return value;
}

function nullableIdentifier(value, name) {
  return value === null ? "" : requiredIdentifier(value, name);
}

function stripIdentityNamespace(value, namespace) {
  return value.startsWith(namespace) ? value.slice(namespace.length) : value;
}

export function calculatorCustomTradeDestinationSignalValueV1(distance) {
  if (typeof distance !== "number" || !Number.isFinite(distance) || distance < 0) {
    throw new TypeError("calculator custom trade distance must be finite and non-negative");
  }
  const rounded = Math.round(distance * 10) / 10;
  return `custom:${rounded.toFixed(1).replace(/\.0$/, "")}`;
}

function acceptedPetSignals(pet, slot) {
  if (pet === undefined) {
    return {
      kind: "catalog",
      displayLabel: null,
      basedOn: "",
      pet: "",
      tier: "",
      packLeader: false,
      special: "",
      talent: "",
      skills: [],
    };
  }
  if (
    !isRecord(pet)
    || !Number.isSafeInteger(pet.tier)
    || pet.tier < 0
    || pet.tier > 255
    || typeof pet.packLeader !== "boolean"
    || !Array.isArray(pet.learnedSkillOptionIds)
    || (pet.kind !== "catalog" && pet.kind !== "custom")
    || (pet.kind === "custom" && pet.basedOn !== null && typeof pet.basedOn !== "string")
    || (pet.kind === "custom" && pet.displayLabel !== null && typeof pet.displayLabel !== "string")
  ) {
    throw new TypeError(`calculator model committed an invalid pet in slot ${slot}`);
  }
  return {
    kind: pet.kind,
    displayLabel: pet.kind === "custom" ? pet.displayLabel : null,
    basedOn: pet.kind === "custom" ? (pet.basedOn ?? "") : "",
    pet: pet.kind === "catalog"
      ? requiredIdentifier(pet.petId, `calculator pet ${slot} ID`)
      : (pet.basedOn ?? ""),
    tier: String(pet.tier),
    packLeader: pet.packLeader,
    special: nullableIdentifier(
      pet.specialOptionId,
      `calculator pet ${slot} special option ID`,
    ),
    talent: nullableIdentifier(
      pet.talentOptionId,
      `calculator pet ${slot} talent option ID`,
    ),
    skills: pet.learnedSkillOptionIds.map((skillId, index) => requiredIdentifier(
      skillId,
      `calculator pet ${slot} skill ${index + 1}`,
    )),
  };
}

export function calculatorAcceptedFixedControlSignalPatchV1(scenario) {
  if (
    !isRecord(scenario)
    || !Array.isArray(scenario.pets)
    || scenario.pets.length > 5
    || !isRecord(scenario.target)
    || !isRecord(scenario.trade)
  ) {
    throw new TypeError("calculator model committed an invalid fixed-control scenario");
  }
  const targetItemId = scenario.target.itemId;
  if (
    targetItemId !== null
    && (
      !Number.isSafeInteger(targetItemId)
      || targetItemId < 1
      || targetItemId > 4_294_967_295
    )
  ) {
    throw new TypeError("calculator model committed an invalid target fish ID");
  }
  const patch = {
    targetFish: targetItemId === null ? "" : String(targetItemId),
  };
  for (let slot = 1; slot <= 5; slot += 1) {
    const pet = acceptedPetSignals(scenario.pets[slot - 1], slot);
    patch[`pet${slot}`] = pet;
    for (let skillSlot = 1; skillSlot <= 3; skillSlot += 1) {
      patch[`_pet${slot}_skill_slot${skillSlot}`] = pet.skills[skillSlot - 1] ?? "";
    }
  }
  patch.tradeOriginRegion = scenario.trade.originId === null
    ? ""
    : stripIdentityNamespace(
      requiredIdentifier(scenario.trade.originId, "calculator trade origin ID"),
      "trade-origin:",
    );
  if (scenario.trade.destination?.kind === "catalog") {
    patch.tradeDestinationNpc = stripIdentityNamespace(
      requiredIdentifier(
        scenario.trade.destination.destinationId,
        "calculator trade destination ID",
      ),
      "trade-destination:",
    );
  } else if (scenario.trade.destination?.kind === "custom") {
    const distance = scenario.trade.destination.distanceBonusPercent;
    patch.tradeDestinationNpc = calculatorCustomTradeDestinationSignalValueV1(distance);
    patch.tradeDistanceBonus = distance;
  } else {
    throw new TypeError("calculator model committed an invalid trade destination");
  }
  return patch;
}
